<template>
  <div class="pt-6 py-8 px-5 bg-white form-float-box-border text-racq-dark-grey">
    <h4 class="text-xl !font-medium leading-7 mb-2"><Text :field="wordings.title" /></h4>
    <DollarAmount :showStarSign="false" :amount="insuranceQuoteSummary ?? ''" />

    <PaymentFrequencyDropdown v-if="paymentStore.paymentFrequencyWording"></PaymentFrequencyDropdown>

    <div class="mb-6">
      <TagList
        :tags="discounts"
        :tagType="TagTypes.discount"
        :tagSize="TagSize.sm"
        :tagOrientation="TagOrientation.vertical"
        innerClass="w-fit"
      />
    </div>
    <template v-if="quoteHasChanged || props.testParams?.enableQuoteHasChanged">
      <button type="button" class="link text-m" @click="showWhyQuoteChangedModal = true">
        <RichText class="flex flex-col space-y-3 text-m mb-6" :field="{ value: premiumHasChangeLink?.value }" />
      </button>
    </template>
    <div
      class="w-full md:w-auto xl:w-full h-8 px-4 py-2 bg-racq-anti-flash-white rounded-lg justify-center items-start gap-5 inline-flex"
    >
      <div class="text-center text-racq-med-grey text-xs leading-none">
        {{ referenceNumberText }}
      </div>
    </div>
  </div>

  <ModalDialog
    :modelValue="showWhyQuoteChangedModal"
    :showXCloseButton="true"
    :closeOnEsc="false"
    size="tooltip"
    @update:modelValue="(value) => onModalClose(value)"
  >
    <div class="sticky-modal-header">{{ whyHasQuoteChangeModal?.fields['modalTitle']?.value }}</div>
    <div class="tooltip-body richtext" v-html="whyHasQuoteChangeModal?.fields['modalBody']?.value"></div>
  </ModalDialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { Text, RichText } from '@sitecore-jss/sitecore-jss-vue';
import { Item } from '@sitecore-jss/sitecore-jss/layout';
import { getDiscountsList } from '@/utils/iq-discounts';
import { IQuote } from '@/interfaces';
import DollarAmount from '@/components/DollarAmount/DollarAmount.vue';
import TagList from '@/components/TagList/TagList.vue';
import { TagTypes, TagOrientation, TagSize } from '@/enums/tag-enums';
import ModalDialog from '../ModalDialog/ModalDialog.vue';
import PaymentFrequencyDropdown from '../PaymentFrequencyDropdown/PaymentFrequencyDropdown.vue';
import { PaymentFrequency } from '@/enums/payment-frequency';
import { usePaymentStore } from '@/store';

enum fieldNames {
  title = 'Title',
  annualPayment = 'Annual Payment',
  monthlyPayment = 'Monthly Payment',
  refNumber = 'Ref Number',
  quoteChangedLink = 'Quote Changed Link',
  whyHasMyQuoteChangedModal = 'Why Has My Quote Changed Modal',
}

const props = defineProps<{
  fields: {
    quote: Partial<IQuote>;
    floatingBoxContext: Item;
    showOnlineDiscount: boolean;
    showOnlineDiscountLast: boolean;
    hasPremiumChanged?: boolean;
  };
  testParams?: {
    enableQuoteHasChanged: boolean;
  };
}>();

const { t, getLocaleMessage } = useI18n();
const showWhyQuoteChangedModal = ref<boolean>(false);
const whyHasQuoteChangeModal = ref<any>();

const getFieldByName = (fieldName: fieldNames) => {
  if (!props.fields.floatingBoxContext?.fields) return;
  const fields = props.fields.floatingBoxContext.fields ? props.fields.floatingBoxContext.fields : [];
  const field = (fields as any[]).find((x: any) => x.name === fieldName);
  return field;
};

const wordings = computed(() => ({
  title: getFieldByName(fieldNames.title)?.content,
  annual: getFieldByName(fieldNames.annualPayment)?.content,
  monthly: getFieldByName(fieldNames.monthlyPayment)?.content,
  refNum: getFieldByName(fieldNames.refNumber)?.content,
}));

const premiumHasChangeLink = ref<any>();
const paymentStore = usePaymentStore();

const referenceNumberText = computed(() => {
  return `${wordings.value.refNum.value} ${props.fields.quote?.reference}`;
});
const insuranceQuoteSummary = computed(() =>
  paymentStore.selectedPaymentFrequency === PaymentFrequency.Annually
    ? props.fields.quote?.insuranceQuoteSummary?.yearlyAnnualPremium
    : props.fields.quote?.insuranceQuoteSummary?.monthlyDeposit
);

const discounts = computed(() =>
  getDiscountsList({ t, getLocaleMessage }, props.fields.quote, {
    showAsMonthly: paymentStore.selectedPaymentFrequency === PaymentFrequency.Monthly,
    showOnlineDiscount: props.fields.showOnlineDiscount,
    showOnlineDiscountLast: props.fields.showOnlineDiscountLast,
  })
);

const quoteHasChanged = computed(() => {
  return props.fields.hasPremiumChanged;
});

onMounted(async () => {
  paymentStore.paymentFrequencyWording = { annual: wordings.value.annual.value, monthly: wordings.value.monthly.value };

  premiumHasChangeLink.value = getFieldByName(fieldNames.quoteChangedLink)?.content;
  whyHasQuoteChangeModal.value = getFieldByName(fieldNames.whyHasMyQuoteChangedModal)?.datasource;
});

function onModalClose(e: any) {
  showWhyQuoteChangedModal.value = e;
}
</script>
